import { TDatabaseId } from '@infotech-soe/metaschema';

import MerpsApiProvider from '../../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import { WorkTableData } from '../../../models/work-table/WorkTableData';
import {
  WorkTableAction,
  WorkTableType,
} from '../../../services/commands/UserInteractor';
import ContextService from '../../../services/context/ContextService';
import WorkTableService from '../../../services/work-table/WorkTableService';
import { AppState } from '../../reducers/root';
import { getCategoryState } from '../../selectors/category';
import { getDatasetCategoryId } from '../../selectors/dataset';
import { getWorkTableState } from '../../selectors/workTable';
import workTable, {
  OpenWorkTablePostEffect,
  OpenWorkTablePostEffectType,
} from '../../slices/workTable';
import { apiCall, createAppListener, postDispatch } from '../listenerHelpers';

const workTableListener = createAppListener();

const apiProvider = new MerpsApiProvider();
const contextService = new ContextService();
const workTableService = new WorkTableService();

const loadData = (
  workerId: TDatabaseId,
  tableType: WorkTableType,
  newDate?: { month: number; year: number }
): Promise<WorkTableData> => {
  if (tableType === WorkTableType.PLAN) {
    return workTableService.loadPlanWorkHours(workerId, newDate);
  } else if (tableType === WorkTableType.FACT) {
    return workTableService.loadFactWorkHours(workerId, newDate);
  } else if (tableType === WorkTableType.CALENDAR) {
    return workTableService.loadTimeSheet(workerId);
  } else {
    return workTableService.loadTimeBoard(workerId);
  }
};

const updateWorkTable = async (state: AppState): Promise<void> => {
  const workerData = getWorkTableState(state);

  if (workerData.changed) {
    await workTableService.update(
      Number(workerData.workerId),
      workerData.data.startDate,
      workerData.data.workHours,
      workerData.data.workDays,
      workerData.tableType
    );
  }
};

workTableListener.startListening(
  apiCall(
    {
      request: workTable.open,
      success: workTable.openSuccess,
      failure: workTable.openFailure,
    },
    async ({ tableType, datasetId, effect }, state) => {
      const categoryId = getDatasetCategoryId(state, datasetId);
      const categoryState = getCategoryState(state, categoryId);
      const datasetsState = state.dataset;

      const context = await contextService.getContext(
        categoryState,
        datasetsState,
        datasetId
      );
      let newDate;
      if (effect.type === OpenWorkTablePostEffectType.FORMAT) {
        const { data: oldData } = getWorkTableState(state);

        newDate = {
          month: oldData.startDate.getMonth(),
          year: oldData.startDate.getFullYear(),
        };

        await apiProvider.actions.executeAction(
          tableType === WorkTableType.PLAN
            ? WorkTableAction.PLAN
            : WorkTableAction.FACT,
          context.toServerContext(),
          {
            DBGN: oldData.startDate,
            DEND: oldData.endDate,
            NMAKEFPV: 1,
            NMAKEFOV: 1,
            NMAKEDAY: 1,
          }
        );
      }
      let data;

      if (effect.type === OpenWorkTablePostEffectType.CHANGE_DATE) {
        await updateWorkTable(state);
        data = await loadData(context.get('Id'), tableType, effect.newDate);
      } else {
        data = await loadData(context.get('Id'), tableType, newDate);
      }

      if (tableType === WorkTableType.CALENDAR) {
        const row = context.get('Row');
        data.startDate = new Date(row.DSTARTDATE);
        data.endDate = new Date(row.DENDDATE);
      }
      if (tableType === WorkTableType.TIME_BOARD) {
        const row = context.get('ParentRow');
        data.startDate = new Date(row.DBGNDATE);
        data.endDate = new Date(row.DENDDATE);
      }

      let redefinedEffect: OpenWorkTablePostEffect;

      if (
        effect.type === OpenWorkTablePostEffectType.CHANGE_DATE ||
        effect.type === OpenWorkTablePostEffectType.FORMAT
      ) {
        redefinedEffect = {
          type: OpenWorkTablePostEffectType.UPDATE,
          workerId: effect.workerId,
          data,
        };
      } else {
        redefinedEffect = effect;
      }

      return {
        context,
        tableType,
        data,
        datasetId,
        effect: redefinedEffect,
      };
    }
  )
);

workTableListener.startListening(
  apiCall(
    {
      request: workTable.update,
      success: workTable.updateSuccess,
      failure: workTable.updateFailure,
    },
    async (_payload, state, dispatch) => {
      await updateWorkTable(state);
      dispatch(workTable.close());
    }
  )
);

workTableListener.startListening(
  postDispatch(workTable.updateWorkDays, (_payload, _state, dispatch) => {
    dispatch(workTable.changed());
  })
);

export default workTableListener.middleware;
